/**
 * Lean client for the slam-core MLB data surface (proxied via slam-mlb
 * backend's /api/statcast and /api/baseball-portal mounts).
 *
 * These helpers return plain objects and throw on HTTP errors — callers
 * are expected to wrap in try/catch and render skeleton/error states.
 * No state, no caching — that's React's job.
 */

async function getJson(url, { signal, allow404 = false } = {}) {
  const res = await fetch(url, { signal, headers: { Accept: 'application/json' } });
  if (!res.ok) {
    if (res.status === 404 && allow404) return null;
    const body = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText}${body ? ` — ${body.slice(0, 200)}` : ''}`);
  }
  return res.json();
}

export function getLeaderboard(metric, { season = 2026, limit = 10, signal } = {}) {
  const qs = new URLSearchParams({ season: String(season), limit: String(limit) });
  // /api/data/* routes to echo (pybaseball → live Statcast). slam-core's
  // /api/statcast/* was natstat-backed and stale — do not fall back to it.
  return getJson(`/api/data/leaderboard/${encodeURIComponent(metric)}?${qs}`, { signal });
}

export function getPitcherProfile(pitcherId, { signal } = {}) {
  return getJson(`/api/statcast/pitcher/${encodeURIComponent(pitcherId)}`, { signal });
}

export function getPitcherPitches(pitcherId, { season = 2026, signal } = {}) {
  const qs = new URLSearchParams({ season: String(season) });
  return getJson(`/api/statcast/pitcher/${encodeURIComponent(pitcherId)}/pitches?${qs}`, { signal });
}

export function listPortalPlayers({ team, position, signal } = {}) {
  const qs = new URLSearchParams();
  if (team) qs.set('team', team);
  if (position) qs.set('position', position);
  const tail = qs.toString();
  return getJson(`/api/baseball-portal/players${tail ? `?${tail}` : ''}`, { signal });
}

export function askNLQuery(question, { signal } = {}) {
  return fetch('/api/baseball-portal/nl-query', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ question }),
    signal,
  }).then((res) => {
    if (!res.ok) throw new Error(`nl-query ${res.status}`);
    return res.json();
  });
}

export function getTeamLeaderboard(teamAbbr, metric, { season = 2026, limit = 25, paired = false, signal } = {}) {
  const qs = new URLSearchParams({ season: String(season), limit: String(limit) });
  if (paired) qs.set('paired', 'true');
  return getJson(
    `/api/data/team/${encodeURIComponent(teamAbbr)}/leaderboard/${encodeURIComponent(metric)}?${qs}`,
    { signal },
  );
}

export function getTeamRoster(teamAbbr, { signal } = {}) {
  return getJson(`/api/data/team/${encodeURIComponent(teamAbbr)}/roster`, { signal });
}

export function getPlayerProfile(mlbamId, { season = 2026, signal } = {}) {
  const qs = new URLSearchParams({ season: String(season) });
  // Most players don't qualify for percentile data this early in the season;
  // a 404 means "no qualifying data yet," not an error. Return null so the
  // cascade tolerates them silently without flooding the console.
  return getJson(`/api/data/player/${encodeURIComponent(mlbamId)}/profile?${qs}`, { signal, allow404: true });
}

export function getPlayerArsenal(mlbamId, { season = 2026, signal } = {}) {
  const qs = new URLSearchParams({ season: String(season) });
  return getJson(`/api/data/player/${encodeURIComponent(mlbamId)}/arsenal?${qs}`, { signal });
}

export function getCatcherStats(mlbamId, { season = 2026, signal } = {}) {
  const qs = new URLSearchParams({ season: String(season) });
  return getJson(`/api/data/player/${encodeURIComponent(mlbamId)}/catching?${qs}`, { signal });
}
